import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'

import { maxTextLength, maxTextTitleLength } from 'tabs/nlp/constants'
import { TrainingStatus } from 'tabs/settings/constants/trainingStatus'

import * as S from './TextareaForm.style'
import { Prompt } from 'react-router-dom'

interface TextFormProps {
  updatedAt?: string
  trainingStatus?: string
  isFromEdit?: boolean
  inputText?: string
  inputDescription?: string
  textAreaContent?: string
  cancelButtonLabel?: string
  submitButtonLabel?: string
  areInitialValues?: boolean
  isResetOnSubmit?: boolean
  setAreInitialValues?: Dispatch<SetStateAction<boolean>>
  onSubmit: (text: string) => Promise<void>
  onCancel: () => void
}

export const TextareaForm: FC<TextFormProps> = ({
  trainingStatus,
  updatedAt,
  cancelButtonLabel,
  submitButtonLabel,
  areInitialValues,
  setAreInitialValues,
  inputDescription,
  isFromEdit = false,
  isResetOnSubmit = false,
  inputText = '',
  textAreaContent = '',
  onCancel,
  onSubmit,
}) => {
  const [textTitle, setTextTitle] = useState(inputText)
  const [text, setText] = useState(textAreaContent)
  const [errorText, setErrorText] = useState('')
  const [errorTitle, setErrorTitle] = useState('')
  const [errorAPI, setErrorAPI] = useState('')

  const callUnsavedChangesAlert = Boolean(text.trim()) || Boolean(textTitle.trim())

  useEffect(() => {
    setAreInitialValues && setAreInitialValues(inputText === textTitle && textAreaContent === text)
    if (callUnsavedChangesAlert && !isFromEdit) window.onbeforeunload = () => true
    if (isFromEdit && !areInitialValues) window.onbeforeunload = () => true

    return () => {
      window.onbeforeunload = null
    }
  }, [text, textTitle, areInitialValues])

  const handleTextChange = e => {
    if (errorAPI) setErrorAPI('')

    const isValidTextLength = e.target.value.length <= maxTextLength

    if (!isValidTextLength) {
      setErrorText(`You've reached the max length for the text value — ${maxTextLength} characters`)
    } else {
      setErrorText('')
    }

    setText(e.target.value)
  }

  const handleSubmit = () => {
    const isValidForm = !errorText && !errorTitle && text
    if (isValidForm) {
      onSubmit(text.trim())
        .then(() => {
          if (isResetOnSubmit) {
            setText('')
            setTextTitle('')
          }
        })
        .catch(error => {
          setErrorAPI(error.message || 'Something went wrong')
        })
    }
  }

  const handleCancel = () => {
    onCancel()
  }

  const textHasChanges = textAreaContent.trim() === text.trim()

  const formError = errorTitle || errorText || errorAPI

  const isSubmitButtonDisabled = !text.trim() || formError || areInitialValues

  return (
    <S.Container isFromEdit={isFromEdit}>
      {!isFromEdit && (
        <Prompt
          when={callUnsavedChangesAlert}
          message="Are you sure you want to leave this page without saving your current changes?"
        />
      )}
      {/*<S.Title isFromEdit={isFromEdit}>{inputDescription || 'Text'}</S.Title>*/}
      <S.Form>
        <S.InputContainer isError={Boolean(formError)} isFromEdit={isFromEdit}>
          {/*<S.TitleInput*/}
          {/*  onChange={handleTitleChange}*/}
          {/*  value={textTitle}*/}
          {/*  placeholder={`${text && !textTitle ? 'Untitled' : 'Enter title'}`}*/}
          {/*/>*/}

          <S.Textarea
            isFromEdit={isFromEdit}
            placeholder="+ Insert text here"
            value={text}
            onChange={handleTextChange}
            isError={formError}
          />
        </S.InputContainer>
        <S.Error>{formError}</S.Error>

        <S.Footer>
          {updatedAt && <small style={{ opacity: 0.75, marginRight: 'auto' }}>Updated at: {updatedAt}</small>}
          <S.ButtonSecondary onClick={onCancel} disabled={isSubmitButtonDisabled}>
            {cancelButtonLabel || 'Cancel'}
          </S.ButtonSecondary>
          <S.Button onClick={handleSubmit} disabled={textHasChanges || isSubmitButtonDisabled}>
            {submitButtonLabel || 'Add text'}
          </S.Button>
        </S.Footer>
      </S.Form>
      {trainingStatus === TrainingStatus.PENDING && <S.DisableScreen />}
    </S.Container>
  )
}
